import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { toast } from "react-toastify";
import { Paginated } from "../../components/Paginated";
import "../../components/table.css";
import CircularProgress from '@mui/material/CircularProgress';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./FOS.module.css";
import "../../components/mcrbTableTheme.css";

const { baseNodeapiUrl, apiEndpoint } = config;
const fosApiUrl = `${baseNodeapiUrl}/fos-api/fos`;

const FOSList = () => {
    const [fosList, setFosList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [sellerList, setSellerList] = useState<any[]>([]);
    const [sellerFilterId, setSellerFilterId] = useState("");
    const [sellersModal, setSellersModal] = useState<{ open: boolean; fosName: string; sellers: any[] }>({ open: false, fosName: "", sellers: [] });

    const fetchFosList = async () => {
        setIsLoading(true);
        try {
            const { data } = await axios({
                url: fosApiUrl,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
                params: {
                    page: 1,
                    limit: 1000,
                    ...(statusFilter ? { status: statusFilter } : {}),
                    ...(search ? { search } : {}),
                },
            });
            if (data.status === 200) {
                setFosList(data.data || []);
            } else {
                toast.error(data.message || "Failed to fetch FOS list");
            }
        } catch (error) {
            console.log("error", error);
            toast.error("Something went wrong while fetching FOS list");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchFosList();
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter]);

    const fetchSellerList = async () => {
        try {
            const { data } = await axios({
                url: `${apiEndpoint}/getsellerlist`,
                method: "GET",
                headers: { "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}` },
            });
            if (data.status === 200) {
                setSellerList(data.data || []);
            }
        } catch (error) {
            console.log("error", error);
        }
    };

    useEffect(() => {
        fetchSellerList();
    }, []);

    const filteredFosList = useMemo(() => {
        if (!sellerFilterId) return fosList;
        return fosList.filter((item: any) =>
            (item.assigned_sellers || []).some((s: any) => String(s.provider_id) === String(sellerFilterId))
        );
    }, [fosList, sellerFilterId]);

    async function toggleStatus(row: any) {
        const nextStatus = !row.status;
        try {
            if (nextStatus) {
                await axios({
                    url: `${fosApiUrl}/${row.id}`,
                    method: "PUT",
                    headers: { "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}` },
                    data: { status: true },
                });
            } else {
                await axios({
                    url: `${fosApiUrl}/${row.id}`,
                    method: "DELETE",
                    headers: { "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}` },
                });
            }
            setFosList((prev) => prev.map((item: any) => (item.id === row.id ? { ...item, status: nextStatus } : item)));
            toast.success(nextStatus ? "FOS activated" : "FOS deactivated");
        } catch (error) {
            console.log("error", error);
            toast.error("Something went wrong");
        }
    }

    function copyCode(code: string) {
        navigator.clipboard?.writeText(code);
        toast.success("Referral code copied");
    }

    function viewAssignedSellers(row: any) {
        setSellersModal({ open: true, fosName: row.name, sellers: row.assigned_sellers || [] });
    }

    function closeSellersModal() {
        setSellersModal({ open: false, fosName: "", sellers: [] });
    }

    const COLUMNS = [
        {
            Header: "Sn.",
            Footer: "Sn.",
            accessor: (row: any, count: any) => <>{count + 1}</>,
            disableFilters: true,
            sticky: "left",
        },
        {
            Header: "Name",
            Footer: "Name",
            accessor: (row: any) => <span style={{ color: "#1F2430", fontWeight: 700, fontFamily: "'Sora', 'Inter', sans-serif" }}>{row.name}</span>,
            sticky: "left",
        },
        {
            Header: "Phone",
            Footer: "Phone",
            accessor: "phone",
            sticky: "left",
        },
        {
            Header: "Email",
            Footer: "Email",
            accessor: (row: any) => row.email || "-",
            sticky: "left",
        },
        {
            Header: "City",
            Footer: "City",
            accessor: (row: any) => row.city || "-",
            sticky: "left",
        },
        {
            Header: "Referral Code",
            Footer: "Referral Code",
            accessor: (row: any) => (
                <span className={styles.referralChip} onClick={() => copyCode(row.referral_code)} title="Click to copy">
                    {row.referral_code} <ContentCopyIcon style={{ fontSize: 13 }} />
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Status",
            Footer: "Status",
            accessor: (row: any) => (
                <span className={row.status ? brandStyles.statusBadgeActive : brandStyles.statusBadgeInactive}>
                    {row.status ? "Active" : "Inactive"}
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Assigned Sellers",
            Footer: "Assigned Sellers",
            accessor: (row: any) =>
                row.assigned_sellers_count ? (
                    <span className={styles.sellersChip} onClick={() => viewAssignedSellers(row)} title="Click to view">
                        {row.assigned_sellers_count} seller{row.assigned_sellers_count > 1 ? "s" : ""}
                    </span>
                ) : (
                    <span style={{ color: "#9CA3AF" }}>-</span>
                ),
            sticky: "left",
        },
        {
            Header: "Action",
            Footer: "Action",
            accessor: (row: any) => (
                <div className={styles.actionCell}>
                    <Link to={`/fos/${row.id}`} className={brandStyles.iconBtn}>
                        <EditNoteRoundedIcon style={{ color: "#4c2fb3" }} titleAccess="Update" />
                    </Link>
                    <span className={brandStyles.iconBtn} onClick={() => toggleStatus(row)}>
                        {row.status ? (
                            <ToggleOffIcon style={{ color: "#EF4444" }} titleAccess="Deactivate" />
                        ) : (
                            <ToggleOnIcon style={{ color: "#16A34A" }} titleAccess="Activate" />
                        )}
                    </span>
                </div>
            ),
            sticky: "left",
        },
    ];

    return (
        <ThemeMaker>
            <div className={`${styles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>FOS</span>
                        <Link to="/fos" className={brandStyles.primaryBtn}>
                            + Add FOS
                        </Link>
                    </div>

                    <div className={styles.toolbarRow}>
                        <input
                            type="search"
                            className={styles.searchInput}
                            placeholder="Search by name, phone, email or referral code"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                        <select className={styles.filterSelect} value={sellerFilterId} onChange={(e) => setSellerFilterId(e.target.value)}>
                            <option value="">All Sellers</option>
                            {
                                sellerList.map((item: any, index: number) => (
                                    <option value={item?.provider_id} key={item?.provider_id || index}>{item?.company_name}</option>
                                ))
                            }
                        </select>
                    </div>

                    <div className={`${brandStyles.bodyArea} ${brandStyles.tableWrap}`} style={{ minHeight: "60vh" }}>
                        {
                            isLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <>
                                    <div className={styles.desktopTable}>
                                        <Paginated data={filteredFosList} columns={COLUMNS} />
                                    </div>

                                    <div className={styles.mobileList}>
                                        {
                                            filteredFosList.map((item: any, index: number) => (
                                                <div className={styles.mobileCard} key={item.id ?? index}>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Name</span>
                                                        <span className={styles.mobileValue}>{item?.name}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Phone</span>
                                                        <span className={styles.mobileValue}>{item?.phone}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>City</span>
                                                        <span className={styles.mobileValue}>{item?.city || "-"}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Referral</span>
                                                        <span className={styles.referralChip} onClick={() => copyCode(item.referral_code)}>
                                                            {item?.referral_code} <ContentCopyIcon style={{ fontSize: 13 }} />
                                                        </span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Status</span>
                                                        <span className={item?.status ? brandStyles.statusBadgeActive : brandStyles.statusBadgeInactive}>
                                                            {item?.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Assigned</span>
                                                        {item?.assigned_sellers_count ? (
                                                            <span className={styles.sellersChip} onClick={() => viewAssignedSellers(item)}>
                                                                {item.assigned_sellers_count} seller{item.assigned_sellers_count > 1 ? "s" : ""}
                                                            </span>
                                                        ) : (
                                                            <span className={styles.mobileValue}>-</span>
                                                        )}
                                                    </div>
                                                    <div className={styles.mobileActionRow}>
                                                        <Link to={`/fos/${item.id}`} className={brandStyles.iconBtn}>
                                                            <EditNoteRoundedIcon style={{ color: "#4c2fb3" }} titleAccess="Update" />
                                                        </Link>
                                                        <span className={brandStyles.iconBtn} onClick={() => toggleStatus(item)}>
                                                            {item.status ? (
                                                                <ToggleOffIcon style={{ color: "#EF4444" }} titleAccess="Deactivate" />
                                                            ) : (
                                                                <ToggleOnIcon style={{ color: "#16A34A" }} titleAccess="Activate" />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>

                                    {filteredFosList.length === 0 ? <p className={styles.emptyState}>No FOS found !</p> : null}
                                </>
                            )
                        }
                    </div>
                </div>

                {sellersModal.open ? (
                    <div className={styles.modalOverlay} onClick={closeSellersModal}>
                        <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <span>Sellers assigned to {sellersModal.fosName}</span>
                                <span className={brandStyles.iconBtn} onClick={closeSellersModal}>✕</span>
                            </div>
                            <div className={styles.modalBody}>
                                {sellersModal.sellers.length === 0 ? (
                                    <p className={styles.emptyState}>No sellers assigned yet !</p>
                                ) : (
                                    sellersModal.sellers.map((s: any) => (
                                        <div className={styles.sellerRow} key={s.id}>
                                            <span className={styles.sellerName}>{s.company_name}</span>
                                            <span className={styles.sellerProviderId}>{s.provider_id}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </ThemeMaker>
    );
};

export default FOSList;
